import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { Bot, Check, Eye, Gavel, MailCheck, Search, Sparkles, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnimatedNumber, EASE, ScoreRing } from './primitives';

const candidates = [
  { name: 'Maya Chen', initials: 'MC', score: 89, avatar: 'bg-bv-blue-wash text-bv-blue' },
  { name: 'Aisha Rahman', initials: 'AR', score: 91, avatar: 'bg-bv-success-soft text-bv-success' },
  { name: 'Daniel Okafor', initials: 'DO', score: 76, avatar: 'bg-bv-orange-soft text-bv-coral' },
  { name: 'Sofia Alvarez', initials: 'SA', score: 84, avatar: 'bg-[hsl(265_80%_95%)] text-[hsl(265_50%_45%)]' },
  { name: 'Priya Nair', initials: 'PN', score: 68, avatar: 'bg-[hsl(42_95%_90%)] text-[hsl(32_80%_32%)]' },
];

const stages = [
  { label: 'Reading resume', short: 'Apply', icon: Search, chip: 'bg-bv-blue-wash text-bv-blue' },
  { label: 'Screened', short: 'Screen', icon: Sparkles, chip: 'bg-[hsl(265_80%_95%)] text-[hsl(265_50%_45%)]' },
  { label: 'Interview sent', short: 'Invite', icon: MailCheck, chip: 'bg-bv-orange-soft text-bv-coral' },
  { label: 'Interviewed', short: 'Interview', icon: Video, chip: 'bg-[hsl(42_95%_90%)] text-[hsl(32_80%_32%)]' },
  { label: 'Ready for review', short: 'Review', icon: Eye, chip: 'bg-bv-success-soft text-bv-success' },
];

const roles = [
  { name: 'Senior Account Manager', count: 214 },
  { name: 'Operations Manager', count: 138 },
  { name: 'Customer Success Lead', count: 96 },
];

function eventText(name: string, stage: number, score: number) {
  const first = name.split(' ')[0];
  return [
    `Reading ${first}’s resume against the role criteria`,
    `${name} scored ${score}% · evidence attached`,
    `Interview invitation sent to ${first}`,
    `${first} answered 5 structured questions`,
    `${name} is ready for recruiter review`,
  ][stage];
}

const STEPS = 11;

function AgentOrb() {
  return (
    <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
      <span className="absolute inset-0 animate-spin-slow rounded-full bg-[conic-gradient(from_0deg,hsl(var(--hero-highlight-start)),hsl(var(--blue)),hsl(var(--hero-highlight-end)),hsl(var(--hero-highlight-start)))]" />
      <span className="absolute inset-[3px] rounded-full bg-white" />
      <Bot className="relative h-4 w-4 text-bv-ink" />
    </span>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-bv-blue-line bg-white px-3 py-2.5">
      <p className="font-display text-xl font-semibold tabular-nums text-bv-ink sm:text-2xl"><AnimatedNumber value={value} format={v => Math.round(v).toString()} /></p>
      <p className="mt-0.5 truncate text-[11px] font-medium text-bv-mute sm:text-xs">{label}</p>
    </div>
  );
}

/** Looping, illustrative "agent at work" product window used in the hero. */
export function AgentConsole() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: '-10% 0px' });
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (reduced) { setStep(8); return; }
    if (!inView) return;
    const id = window.setInterval(() => setStep(s => (s + 1) % STEPS), 1500);
    return () => window.clearInterval(id);
  }, [reduced, inView]);

  const rows = candidates.map((c, i) => ({ ...c, stage: Math.min(Math.max(step - i, 0), 4), visible: step >= i }));
  const visibleRows = rows.filter(r => r.visible);
  const events = rows
    .map((r, i) => ({ r, stage: step - i }))
    .filter(({ stage }) => stage >= 0 && stage <= 4)
    .reverse()
    .slice(0, 3)
    .map(({ r, stage }) => ({ key: `${r.name}-${stage}`, text: eventText(r.name, stage, r.score) }));
  const feed = events.length ? events : [{ key: 'done', text: 'Shortlist ready · 5 candidates waiting for your recruiter' }];

  return (
    <div ref={ref} className="relative" data-prerender="skip">
      <div className="relative overflow-hidden rounded-[26px] border border-white bg-white/90 shadow-[0_50px_120px_-50px_hsl(var(--navy)/0.55)] ring-1 ring-bv-blue-line backdrop-blur-xl">
        <div className="flex items-center gap-3 border-b border-bv-blue-line bg-bv-blue-wash/70 px-4 py-3">
          <div className="flex gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          </div>
          <div className="mx-auto hidden rounded-md border border-bv-blue-line bg-white px-3 py-1 text-xs font-medium text-bv-mute sm:block">platform.beatview.ai</div>
          <span className="ml-auto rounded-md bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral sm:ml-0">Example data</span>
        </div>

        <div className="grid md:grid-cols-[190px_minmax(0,1fr)]">
          <aside className="hidden border-r border-bv-blue-line bg-bv-blue-wash/40 p-4 md:block">
            <div className="flex items-center gap-2.5">
              <AgentOrb />
              <div>
                <p className="text-sm font-semibold text-bv-ink">Beatview agent</p>
                <p className="flex items-center gap-1.5 text-xs font-medium text-bv-success">
                  <span className="relative flex h-1.5 w-1.5"><span className="absolute inset-0 animate-ping rounded-full bg-bv-success opacity-70" /><span className="relative h-1.5 w-1.5 rounded-full bg-bv-success" /></span>
                  Working
                </p>
              </div>
            </div>
            <p className="mt-6 text-[11px] font-semibold uppercase tracking-wider text-bv-mute">Active roles</p>
            <ul className="mt-2 space-y-1">
              {roles.map((role, i) => (
                <li key={role.name} className={cn('rounded-lg px-2.5 py-2 text-[13px]', i === 0 ? 'bg-white font-semibold text-bv-ink shadow-bv-card' : 'text-bv-ink2')}>
                  <span className="block truncate">{role.name}</span>
                  <span className="text-[11px] font-medium text-bv-mute">{role.count} applicants</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 rounded-xl border border-bv-blue-line bg-white p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-bv-mute"><Gavel className="h-3 w-3" />Recruiter</p>
              <p className="mt-1 text-[13px] font-semibold leading-5 text-bv-ink">Makes every hiring decision</p>
            </div>
          </aside>

          <div className="min-w-0 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-bv-mute">Role</p>
                <p className="truncate font-display text-lg font-semibold text-bv-ink">Senior Account Manager</p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-bv-blue-line bg-white px-2.5 py-1 text-xs font-semibold text-bv-ink2"><Bot className="h-3.5 w-3.5 text-bv-blue" />Agent on</span>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              <Stat label="Applications read" value={186 + step * 3} />
              <Stat label="Interviews scored" value={12 + rows.filter(r => r.visible && r.stage >= 3).length} />
              <Stat label="Ready for review" value={rows.filter(r => r.visible && r.stage === 4).length} />
            </div>

            <div className="mt-4 flex gap-1.5" aria-hidden>
              {stages.map((s, i) => {
                const reached = rows.some(r => r.visible && r.stage >= i);
                return (
                  <div key={s.short} className="min-w-0 flex-1">
                    <div className="h-1.5 overflow-hidden rounded-full bg-bv-blue-line">
                      <motion.div className="bv-btn-gradient h-full rounded-full" initial={false} animate={{ width: reached ? '100%' : '0%' }} transition={{ duration: 0.6, ease: EASE }} />
                    </div>
                    <p className={cn('mt-1.5 hidden truncate text-[11px] font-semibold sm:block', reached ? 'text-bv-ink' : 'text-bv-mute')}>{s.short}</p>
                  </div>
                );
              })}
            </div>

            <ul className="mt-4 min-h-[318px] space-y-2" aria-label="Example candidates processed by the agent">
              <AnimatePresence initial={false}>
                {visibleRows.map(row => {
                  const stage = stages[row.stage];
                  return (
                    <motion.li
                      key={row.name}
                      layout
                      initial={{ opacity: 0, y: 14, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, transition: { duration: 0.2 } }}
                      transition={{ duration: 0.45, ease: EASE }}
                      className={cn('relative flex h-[58px] items-center gap-3 overflow-hidden rounded-xl border bg-white px-3', row.stage === 0 ? 'border-bv-blue/40' : 'border-bv-blue-line')}
                    >
                      {row.stage === 0 && !reduced && (
                        <motion.span aria-hidden className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-bv-blue/10 to-transparent" initial={{ x: '-100%' }} animate={{ x: '320%' }} transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }} />
                      )}
                      <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold', row.avatar)}>{row.initials}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-bv-ink">{row.name}</p>
                        <motion.span key={row.stage} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} className={cn('mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', stage.chip)}>
                          <stage.icon className="h-3 w-3" />{stage.label}
                        </motion.span>
                      </div>
                      {row.stage === 0
                        ? <span className="flex h-11 w-11 items-center justify-center" aria-label="Scoring"><span className="h-4 w-4 animate-spin rounded-full border-2 border-bv-blue border-t-transparent" /></span>
                        : <ScoreRing score={row.score} size={44} stroke={4} tone={row.score >= 80 ? 'success' : row.score >= 72 ? 'blue' : 'warn'} />}
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>

            <div className="mt-4 rounded-xl bg-bv-ink p-3 text-white">
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/50"><Sparkles className="h-3 w-3 text-bv-blue-bright" />Agent activity</p>
              <ul className="relative mt-2 h-[66px] overflow-hidden text-[13px]" aria-live="off">
                <AnimatePresence initial={false} mode="popLayout">
                  {feed.map(event => (
                    <motion.li key={event.key} layout initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} className="flex h-[22px] items-center gap-2">
                      <Check className="h-3.5 w-3.5 shrink-0 text-bv-success" />
                      <span className="truncate text-white/85">{event.text}</span>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div aria-hidden className="absolute -left-10 top-[132px] hidden animate-float-slow xl:block">
        <div className="w-56 rounded-2xl border border-bv-blue-line bg-white/95 p-3 shadow-bv-window backdrop-blur">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-bv-success"><Check className="h-3 w-3" />Evidence attached</p>
          <p className="mt-1.5 text-[13px] font-medium leading-5 text-bv-ink">“Led renewals across enterprise accounts”</p>
        </div>
      </div>
      <div aria-hidden className="absolute -bottom-6 -right-6 hidden animate-float-slow [animation-delay:-3s] xl:block">
        <div className="flex items-center gap-2.5 rounded-2xl bg-bv-ink px-4 py-3 text-white shadow-bv-deep">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bv-btn-gradient"><Gavel className="h-4 w-4" /></span>
          <div><p className="text-[13px] font-semibold">You decide who moves forward</p><p className="text-[11px] text-white/60">The agent never makes the hire</p></div>
        </div>
      </div>
    </div>
  );
}
